import { motion } from "framer-motion";
import { ArrowRight, CalendarClock, Check } from "lucide-react";
import Marquee from "react-fast-marquee";
import { Button } from "@/components/ui/button";
import { IMAGES, TRUST_BADGES, SITE } from "@/site/content";

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 bg-white overflow-hidden"
    >
      <div className="absolute inset-0 bg-dot-grid opacity-60 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 bg-white text-xs font-medium text-gray-700 mb-6"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-blue-700" />
              Websites for local businesses — free demo before you pay
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight text-gray-900 leading-[1.05]"
            >
              Get your business online and{" "}
              <span className="text-blue-700">start winning more customers.</span>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-6 text-base md:text-lg text-gray-600 max-w-2xl leading-relaxed"
            >
              Most customers search online before they call. If your business
              doesn't have a website, you're losing opportunities every day.
              We build affordable, professional sites for local owners — and
              we'll show you yours <strong className="text-gray-900">before you spend a dollar</strong>.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="hero-primary-cta"
                size="lg"
                onClick={() => scrollTo("#demo")}
                className="bg-blue-700 hover:bg-blue-800 text-white h-12 px-6 rounded-md group"
              >
                {SITE.ctas.primary}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
              <Button
                asChild
                data-testid="hero-secondary-cta"
                size="lg"
                variant="outline"
                className="h-12 px-6 rounded-md border-gray-300 text-gray-900 hover:bg-gray-50"
              >
                <a
                  href={SITE.scheduleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <CalendarClock className="mr-2 h-4 w-4" />
                  {SITE.ctas.secondary}
                </a>
              </Button>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-600">
              {["No payment up front", "Built for owners, not techies", "Live in under 2 weeks"].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-blue-700" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5"
            data-testid="hero-visual"
          >
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden border border-gray-200 shadow-2xl bg-white">
                <img
                  src={IMAGES.hero_mockup}
                  alt="Modern website displayed on a screen"
                  className="w-full h-auto object-cover"
                  loading="eager"
                />
              </div>
              {/* Mobile mockup overlay */}
              <div className="hidden md:block absolute -bottom-8 -left-8 w-40 rounded-2xl overflow-hidden border border-gray-200 shadow-xl bg-white">
                <img
                  src={IMAGES.portfolio_mobile}
                  alt="Mobile website example"
                  className="w-full h-auto object-cover"
                />
              </div>
              {/* Floating trust card */}
              <div className="absolute -top-4 -right-4 hidden md:flex items-center gap-3 bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3">
                <div className="h-9 w-9 rounded-full bg-blue-700/10 flex items-center justify-center">
                  <span className="text-blue-700 font-bold text-sm">★ 4.9</span>
                </div>
                <div className="text-xs leading-tight">
                  <div className="font-semibold text-gray-900">Owners love it</div>
                  <div className="text-gray-500">From 60+ local clients</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Trust marquee */}
        <div className="mt-16 md:mt-20" data-testid="hero-trust-marquee">
          <div className="text-xs uppercase tracking-widest text-gray-500 mb-4 text-center">
            Built for local businesses — every site includes
          </div>
          <div className="marquee-mask">
            <Marquee gradient={false} speed={32} pauseOnHover>
              {TRUST_BADGES.concat(TRUST_BADGES).map((b, i) => (
                <div
                  key={i}
                  className="mx-6 flex items-center gap-2 text-gray-800"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-700" />
                  <span className="font-display font-bold text-lg md:text-xl">
                    {b}
                  </span>
                </div>
              ))}
            </Marquee>
          </div>
        </div>
      </div>
    </section>
  );
};
